import { MediaMatcher } from '@angular/cdk/layout';
import {
  afterNextRender,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  Injectable,
  linkedSignal,
  RendererFactory2,
  signal,
} from '@angular/core';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { ThemeType } from './types';

type ActiveTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeSwitcherService {
  #destroyRef = inject(DestroyRef);
  #document = inject(DOCUMENT);
  #mediaMatcher = inject(MediaMatcher);
  #rendererFactory = inject(RendererFactory2);
  #renderer = this.#rendererFactory.createRenderer(null, null);

  isDarkMode = computed(() => this.activeTheme() === 'dark');

  #userPreferedTheme = injectLocalStorage<ActiveTheme | null>('currentTheme');

  #systemPrefersDark = signal(
    this.#mediaMatcher.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  userPreference = linkedSignal<ThemeType>(() => {
    const userPerferedTheme = this.#userPreferedTheme();
    return userPerferedTheme ? userPerferedTheme : 'system';
  });

  activeTheme = computed<ActiveTheme>(() => {
    const userPreferedTheme = this.#userPreferedTheme();
    const systemPrefersDark = this.#systemPrefersDark();

    if (userPreferedTheme) {
      return userPreferedTheme;
    }

    const preference = this.userPreference();

    if (preference === 'system') {
      return systemPrefersDark ? 'dark' : 'light';
    }

    return preference as ActiveTheme;
  });

  setTheme(theme: ThemeType) {
    this.userPreference.set(theme);
  }

  toggle() {
    const current = this.userPreference();
    const next: ThemeType =
      current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    this.setTheme(next);
  }

  userPreferenceEffect = effect(() => {
    const preference = this.userPreference();
    if (preference === 'system') {
      this.#userPreferedTheme.set(null);
    } else {
      this.#userPreferedTheme.set(preference);
    }
  });

  setThemeEffect = effect(() => {
    const theme = this.activeTheme();

    this.#renderer.setAttribute(
      this.#document.documentElement,
      'data-theme',
      theme,
    );
  });

  constructor() {
    const mediaQuery = this.#mediaMatcher.matchMedia(
      '(prefers-color-scheme: dark)',
    );

    const themePreferencesChangeHandler = (e: MediaQueryListEvent) => {
      this.#systemPrefersDark.set(e.matches);
    };

    let prefersColorHandler: () => void = () => void 0;

    afterNextRender(() => {
      prefersColorHandler = this.#renderer.listen(
        mediaQuery,
        'change',
        themePreferencesChangeHandler,
      );

      this.#destroyRef.onDestroy(() => {
        prefersColorHandler();
      });
    });
  }
}
